/**
 * @file AddMyCardSheet.tsx
 * @brief Add a personal card: SetupIntent + Stripe Payment Element, 3D Secure
 *        in the page (Stripe.js opens the bank's challenge in a frame).
 *
 * @details
 * `redirect: "if_required"` keeps card 3DS inside the page, which matters in
 * the Mac app: a full-page redirect would leave the app's origin and the
 * desktop shell sends any foreign page to the real browser (DESKTOP main.js
 * will-navigate). The new card is made default when it is the first card or
 * when the caller asks (Plan → "Add a card and continue").
 * Errors are Stripe's own message under a plain title (declines, a failed
 * challenge). The header ✕ closes the sheet, so there is no Cancel.
 *
 * @author DES
 * @date 17-10-2026
 */

import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import type { StripeError, StripePaymentElementOptions } from "@stripe/stripe-js";
import { LoaderCircle } from "lucide-react";
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/Button";
import {
  FormSheet,
  FormSheetBody,
  FormSheetContent,
  FormSheetDescription,
  FormSheetField,
  FormSheetFields,
  FormSheetFooter,
  FormSheetFooterCell,
  FormSheetFrame,
  FormSheetHeader,
  FormSheetSection,
  FormSheetTitle,
  formSheetActionClass,
} from "@/components/ui/FormSheet";
import { ApiClientError } from "@/shared/api/client";
import { useAuth } from "@/shared/auth/auth-context";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import { getStripe } from "@/shared/stripe/stripeClient";
import { createMySetupIntent, myBillingKeys, setMyDefaultCard } from "../api";
import { billingErrorView } from "../billingErrors";

const OPTIONS: StripePaymentElementOptions = {
  layout: "tabs",
  // Apple Pay / Google Pay where the browser has them (18-10-2026); they save
  // a card for off-session use like a typed card. Link stays off.
  wallets: { applePay: "auto", googlePay: "auto", link: "never" },
  // Only "never" what confirmSetup passes itself (name): Stripe refuses to
  // confirm when a field is "never" and not passed (IntegrationError, found in
  // the live test 17-10-2026). A card never asks for email or phone anyway.
  fields: { billingDetails: { name: "never", address: "auto" } },
};

/** Stripe's error in our words where we have them, else Stripe's message. */
function cardErrorText(error: StripeError): { title: string; message: string } {
  const code = error.decline_code || error.code;
  const view = billingErrorView(code, "personal");
  if (view) return { title: view.title, message: error.message || view.message };
  return { title: "Card not saved", message: error.message ?? "Please check your card details." };
}

function CardForm({
  submitRef,
  makeDefault,
  setSaving,
  onSaved,
  ready,
  onReady,
}: {
  submitRef: MutableRefObject<(() => Promise<void>) | null>;
  makeDefault: boolean;
  setSaving: (v: boolean) => void;
  onSaved: (paymentMethodId: string | null) => void;
  ready: boolean;
  onReady: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const { user } = useAuth();
  const qc = useQueryClient();
  const name =
    user?.name || [user?.firstName, user?.lastName].filter(Boolean).join(" ") || user?.email || "Card holder";

  useEffect(() => {
    submitRef.current = async () => {
      if (!stripe || !elements) return;
      setSaving(true);
      try {
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href,
            payment_method_data: { billing_details: { name, email: user?.email ?? undefined } },
          },
          redirect: "if_required",
        });
        if (error) {
          const t = cardErrorText(error);
          toast({ variant: "destructive", title: t.title, description: t.message });
          return;
        }
        if (setupIntent?.status !== "succeeded") {
          toast({
            variant: "destructive",
            title: "Card not saved",
            description: "Your bank didn't confirm the card. Try again or use another card.",
          });
          return;
        }
        const pm = setupIntent.payment_method;
        const pmId = typeof pm === "string" ? pm : (pm?.id ?? null);
        if (makeDefault && pmId) await setMyDefaultCard(pmId);
        await qc.invalidateQueries({ queryKey: myBillingKeys.cards() });
        toast({ variant: "success", title: "Card saved" });
        onSaved(pmId);
      } catch (err) {
        console.error("[AddMyCardSheet] save failed:", err);
        toast({
          variant: "destructive",
          title: "Card not saved",
          description:
            err instanceof ApiClientError || err instanceof Error ? err.message : "Please try again.",
        });
      } finally {
        setSaving(false);
      }
    };
    return () => {
      submitRef.current = null;
    };
  }, [elements, makeDefault, name, onSaved, qc, setSaving, stripe, submitRef, user?.email]);

  // Stripe's form takes several seconds to appear (seen in the live test,
  // 17-10-2026); keep a spinner until it is ready instead of an empty sheet.
  return (
    <>
      {!ready ? (
        <div className="flex justify-center py-10">
          <LoaderCircle className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : null}
      <div className={ready ? undefined : "h-0 overflow-hidden"}>
        <PaymentElement options={OPTIONS} onReady={() => onReady()} />
      </div>
    </>
  );
}

export function AddMyCardSheet({
  open,
  onOpenChange,
  makeDefault,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  makeDefault: boolean;
  onSaved?: (paymentMethodId: string | null) => void;
}) {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [formReady, setFormReady] = useState(false);
  const submitRef = useRef<(() => Promise<void>) | null>(null);

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setSaving(false);
      setFormReady(false);
      return;
    }
    let cancelled = false;
    void createMySetupIntent()
      .then((s) => {
        if (!cancelled) setClientSecret(s);
      })
      .catch((err) => {
        if (cancelled) return;
        toast({
          variant: "destructive",
          title: "Couldn't start card setup",
          description: err instanceof ApiClientError ? err.message : "Please try again.",
        });
        onOpenChange(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- a fresh intent each open
  }, [open]);

  return (
    <FormSheet open={open} onOpenChange={(v) => !saving && onOpenChange(v)}>
      <FormSheetContent side="palette">
        <FormSheetFrame>
          <FormSheetHeader>
            <FormSheetTitle>Add a card</FormSheetTitle>
            <FormSheetDescription>
              Used for your plan and pay-as-you-go use. Stripe holds the card details; we never see the number.
            </FormSheetDescription>
          </FormSheetHeader>
          <FormSheetBody>
            <FormSheetSection title="Card">
              <FormSheetFields>
                <FormSheetField span={2}>
                  {!clientSecret ? (
                    <div className="flex justify-center py-10">
                      <LoaderCircle className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <Elements stripe={getStripe()} options={{ clientSecret }}>
                      <CardForm
                        submitRef={submitRef}
                        makeDefault={makeDefault}
                        setSaving={setSaving}
                        ready={formReady}
                        onReady={() => setFormReady(true)}
                        onSaved={(pmId) => {
                          onOpenChange(false);
                          onSaved?.(pmId);
                        }}
                      />
                    </Elements>
                  )}
                </FormSheetField>
              </FormSheetFields>
            </FormSheetSection>
          </FormSheetBody>
          <FormSheetFooter>
            <FormSheetFooterCell>
              <Button
                type="button"
                disabled={saving || !clientSecret || !formReady}
                onClick={() => void submitRef.current?.()}
                className={cn(formSheetActionClass, "bg-primary text-primary-foreground hover:bg-primary/90")}
              >
                {saving ? (
                  <>
                    <LoaderCircle className="h-4 w-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  "Save card"
                )}
              </Button>
            </FormSheetFooterCell>
          </FormSheetFooter>
        </FormSheetFrame>
      </FormSheetContent>
    </FormSheet>
  );
}
